import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../services/api';
import {
  Users, PawPrint, QrCode, MapPin, Phone, ClipboardList, Store, Package,
  ArrowRight, BarChart3,
} from 'lucide-react';
import { UserHeader } from '../../components/layout/UserHeader';

const StatCard = ({ icon: Icon, label, value, color, to }) => (
  <Link
    to={to}
    className="sp-card"
    style={{
      textAlign: 'center',
      textDecoration: 'none',
      color: 'inherit',
      transition: 'transform 0.15s, box-shadow 0.15s',
      display: 'block',
    }}
    onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; }}
    onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
  >
    <Icon size={28} style={{ color, marginBottom: '0.5rem' }} />
    <div style={{ fontSize: '1.8rem', fontWeight: 800, color: 'var(--sp-text)' }}>{value}</div>
    <div style={{ fontSize: '0.85rem', color: 'var(--sp-muted)', fontWeight: 600 }}>{label}</div>
  </Link>
);

const extractData = (response) => {
  const payload = response.data;
  return Array.isArray(payload?.data) ? payload.data : (Array.isArray(payload) ? payload : []);
};

const tiempoRelativo = (fecha) => {
  if (!fecha) return '—';
  const d = new Date(fecha.replace(' ', 'T'));
  if (isNaN(d.getTime())) return fecha;
  const min = Math.floor((Date.now() - d.getTime()) / 60000);
  if (min < 1) return 'Hace instantes';
  if (min < 60) return `Hace ${min} min`;
  const horas = Math.floor(min / 60);
  if (horas < 24) return `Hace ${horas} h`;
  return `Hace ${Math.floor(horas / 24)} día(s)`;
};

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    usuarios: 0, mascotas: 0, codigos: 0, ubicaciones: 0,
    contactos: 0, historial: 0, socios: 0, productos: 0,
  });
  const [ubicacionesData, setUbicacionesData] = useState([]);
  const [mascotasData, setMascotasData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [
          resUsuarios, resMascotas, resCodigos, resUbicaciones,
          resContactos, resHistorial, resSocios, resProductos,
        ] = await Promise.all([
          api.getUsuarios().catch(() => ({ data: { data: [] } })),
          api.getMascotas().catch(() => ({ data: { data: [] } })),
          api.getCodigos().catch(() => ({ data: { data: [] } })),
          api.getUbicaciones().catch(() => ({ data: { data: [] } })),
          api.getContactos().catch(() => ({ data: { data: [] } })),
          api.getHistorial().catch(() => ({ data: { data: [] } })),
          api.getSocios().catch(() => ({ data: { data: [] } })),
          api.getProductos().catch(() => ({ data: { data: [] } })),
        ]);

        const ubicaciones = extractData(resUbicaciones);
        const mascotas = extractData(resMascotas);

        setUbicacionesData(ubicaciones);
        setMascotasData(mascotas);

        setStats({
          usuarios: extractData(resUsuarios).length,
          mascotas: mascotas.length,
          codigos: extractData(resCodigos).length,
          ubicaciones: ubicaciones.length,
          contactos: extractData(resContactos).length,
          historial: extractData(resHistorial).length,
          socios: extractData(resSocios).length,
          productos: extractData(resProductos).length,
        });
      } catch (err) {
        console.error('Error cargando estadísticas:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  const actividadReciente = useMemo(() => {
    return [...ubicacionesData]
      .sort((a, b) => new Date(b.fecha_hora) - new Date(a.fecha_hora))
      .slice(0, 5)
      .map(u => ({
        ...u,
        nombreMascota: mascotasData.find(m => String(m.id) === String(u.id_mascota))?.nombre || `Mascota #${u.id_mascota}`,
      }));
  }, [ubicacionesData, mascotasData]);

  if (loading) return <div className="sp-card">Cargando estadísticas...</div>;

  return (
    <>
      {/* <UserHeader /> */}
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '0.75rem' }}>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 800, margin: 0 }}>Panel de Administración</h1>
          <Link to="/admin/estadisticas" className="sp-btn sp-btn--primary">
            <BarChart3 size={18} /> Ver estadísticas
          </Link>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
          <StatCard icon={Users} label="Usuarios" value={stats.usuarios} color="#4e3f7f" to="/admin/usuarios" />
          <StatCard icon={PawPrint} label="Mascotas" value={stats.mascotas} color="#d16fa3" to="/admin/mascotas" />
          <StatCard icon={QrCode} label="Códigos QR" value={stats.codigos} color="#5b4a8b" to="/admin/codigos" />
          <StatCard icon={MapPin} label="Ubicaciones" value={stats.ubicaciones} color="#16a34a" to="/admin/ubicaciones" />
          <StatCard icon={Phone} label="Contactos" value={stats.contactos} color="#d97706" to="/admin/contactos" />
          <StatCard icon={ClipboardList} label="Historial" value={stats.historial} color="#0ea5e9" to="/admin/historial" />
          <StatCard icon={Store} label="Socios" value={stats.socios} color="#8b5cf6" to="/admin/socios" />
          <StatCard icon={Package} label="Productos" value={stats.productos} color="#ec4899" to="/admin/productos" />
        </div>

        <div className="sp-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 800 }}>Actividad reciente</h3>
            <Link to="/admin/ubicaciones" style={{ fontSize: '0.82rem', color: 'var(--sp-primary)', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '0.2rem', textDecoration: 'none' }}>
              Ver todo <ArrowRight size={14} />
            </Link>
          </div>

          {actividadReciente.length === 0 ? (
            <p style={{ color: 'var(--sp-muted)', margin: 0 }}>Todavía no hay escaneos registrados.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
              {actividadReciente.map(u => (
                <div key={u.id} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', fontSize: '0.88rem' }}>
                  <MapPin size={16} style={{ color: '#16a34a', flexShrink: 0 }} />
                  <span style={{ fontWeight: 700 }}>{u.nombreMascota}</span>
                  <span style={{ color: 'var(--sp-muted)' }}>{u.lugar || 'ubicación sin resolver'}</span>
                  <span style={{ marginLeft: 'auto', color: 'var(--sp-muted)', fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                    {tiempoRelativo(u.fecha_hora)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default AdminDashboard;